import { PseudoClassDecoratorConfig } from '../../models/types';

/**
 * Pseudo class decorator that is used for stencil lifecycle components.
 *
 * This is experimental decorator factory that acts as a pseudo class decorator. It is specifically
 * used for decorating multiple stencil lifecycle hooks. This is useful for simplifying solutions
 * that may require multiple decorators to work. The decorator can be applied to any method or property.
 * Because it is a pseudo class decorator it'll apply the decorators independent of where it is
 * placed.
 *
 */
export class LifecycleDecoratorFactory {
  protected static decorate(configs: PseudoClassDecoratorConfig[]) {
    return function (
      componentInstance: unknown,
      key: string,
      propertyDescriptor?: PropertyDescriptor
    ): void {
      LifecycleDecoratorFactory.applyDecorations(
        componentInstance,
        key,
        propertyDescriptor,
        configs
      );
    };
  }

  private static applyDecorations(
    componentInstance: unknown,
    key: string,
    propertyDescriptor: PropertyDescriptor,
    configs: PseudoClassDecoratorConfig[]
  ) {
    for (const config of configs) {
      const params = LifecycleDecoratorFactory.getDecorateMethodParams(
        componentInstance,
        key,
        propertyDescriptor,
        config
      );
      LifecycleDecoratorFactory.decorateMethod(...params);
    }
  }

  private static getDecorateMethodParams(
    componentInstance: unknown,
    key: string,
    propertyDescriptor: PropertyDescriptor,
    config: PseudoClassDecoratorConfig
  ): [
    instanceOrDescriptor: unknown,
    key: string,
    config: PseudoClassDecoratorConfig,
    decoratorWasAppliedToMethod: boolean,
    decoratedMethodKey: string,
  ] {
    const decoratorWasAppliedToMethod = key === config.name;
    if (decoratorWasAppliedToMethod) {
      return [propertyDescriptor, 'value', config, decoratorWasAppliedToMethod, key];
    }

    return [componentInstance, config.name, config, decoratorWasAppliedToMethod, key];
  }

  private static decorateMethod(
    instanceOrDescriptor: unknown,
    key: string,
    config: PseudoClassDecoratorConfig,
    decoratorWasAppliedToMethod: boolean,
    decoratedMethodKey: string
  ) {
    const originalMethod = instanceOrDescriptor[key];

    instanceOrDescriptor[key] = function (...args: unknown[]) {
      config.callback.call(this, decoratedMethodKey);

      if (decoratorWasAppliedToMethod) {
        return originalMethod.apply(this, args);
      } else {
        return originalMethod?.call(this);
      }
    };
  }
}
